import React, { useMemo } from 'react';
import MessageItem from './MessageItem';
import { buildTimelineMessageGroups } from '../../utils/meetingUtils';

// Helper to format date badges
const formatDateBadge = (date) => {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const isSameDay = (d1, d2) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();

  if (isSameDay(date, now)) return 'Today';
  if (isSameDay(date, yesterday)) return 'Yesterday';

  const isCurrentYear = date.getFullYear() === now.getFullYear();
  const options = { day: 'numeric', month: 'short' };
  if (!isCurrentYear) {
    options.year = 'numeric';
  }

  return date.toLocaleDateString(undefined, options);
};

const MessageList = ({ messages, currentUserId, isDirectMessage, onAddReaction, onEditMessage, onDeleteMessage, onReply, onViewThread, onPreviewThread, onCancelThreadHoverOpen, onForward, onScrollToMessage, onJumpTimelineUp, onJumpTimelineDown, messageRefs, onRetryMessage, onMeetingMessageUpdated, onEditEvent, onCloneEvent, cloningEventId, expandCallNotesMeetingId, onJoinDirectCall, reminderPanelOpen, allChannelMessages, searchHighlightQuery = '', activeSearchMatchMessageId = null, assignableUsers = [], chatMappingActive = false, onMapMessage, emptyHint = null, chatStartLabel = null, channelId }) => {
  const timelineGroups = useMemo(
    () => buildTimelineMessageGroups(messages || []),
    [messages],
  );

  const dayGroups = useMemo(() => {
    if (!timelineGroups.length) return [];

    const days = [];
    let currentDayKey = null;

    timelineGroups.forEach((group) => {
      const anchor = group.messages[group.messages.length - 1];
      const date = new Date(anchor.createdAt || anchor.timestamp || 0);
      const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

      if (dayKey !== currentDayKey) {
        days.push({ dayKey, date, groups: [group] });
        currentDayKey = dayKey;
      } else {
        days[days.length - 1].groups.push(group);
      }
    });

    return days;
  }, [timelineGroups]);

  const chatStartBanner = chatStartLabel ? (
    <div className="flex justify-center py-3 px-4" data-chat-start="true">
      <p className="text-center text-[12px] leading-5 text-gray-500 dark:text-gray-400">
        {chatStartLabel}
      </p>
    </div>
  ) : null;

  if (!messages || messages.length === 0) {
    if (chatStartBanner) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] py-10">
          {chatStartBanner}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-full py-10">
        <div className="text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm px-8 py-10 max-w-sm">
          <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-white">
            {emptyHint?.title || 'No messages yet'}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {emptyHint?.description || 'Be the first to send a message in this channel'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-2">
      {chatStartBanner}
      {dayGroups.map((dayGroup, dayIndex) => (
        <div key={dayGroup.dayKey} className="relative mb-5 last:mb-0">
          <div className="sticky top-0 z-30 flex justify-center pointer-events-none py-2">
            <div className="pointer-events-auto inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold bg-white/95 dark:bg-gray-800/95 text-gray-500 dark:text-gray-400 rounded-full shadow-md border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-md transition-all duration-200 uppercase tracking-wider">
              {onJumpTimelineUp && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onJumpTimelineUp();
                  }}
                  className="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200"
                  title="Jump 10 messages up"
                  aria-label="Jump 10 messages up"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              )}
              <span>{formatDateBadge(dayGroup.date)}</span>
              {onJumpTimelineDown && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onJumpTimelineDown();
                  }}
                  className="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200"
                  title="Jump 10 messages down"
                  aria-label="Jump 10 messages down"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {dayGroup.groups.map((group, groupIndex) => {
              const isLastInTimeline = dayIndex === dayGroups.length - 1
                && groupIndex === dayGroup.groups.length - 1;
              const firstMessage = group.messages[0];
              const isCallOrganizer = group.isCallGroup && (
                firstMessage.meetingInfo?.startedByUserId === currentUserId
                || firstMessage.senderId === currentUserId
              );
              const isOwn = group.isCallGroup
                ? isCallOrganizer
                : firstMessage.senderId === currentUserId;
              const stableKey = firstMessage.tempId || firstMessage.id || `group-${dayGroup.dayKey}-${groupIndex}`;

              return (
                <MessageItem
                  key={stableKey}
                  messages={group.messages}
                  message={firstMessage}
                  isOwn={isOwn}
                  isCallGroup={group.isCallGroup}
                  isLastInTimeline={isLastInTimeline}
                  isDirectMessage={isDirectMessage}
                  currentUserId={currentUserId}
                  onAddReaction={onAddReaction}
                  onEditMessage={onEditMessage}
                  onDeleteMessage={onDeleteMessage}
                  onReply={onReply}
                  onViewThread={onViewThread}
                  onPreviewThread={onPreviewThread}
                  onCancelThreadHoverOpen={onCancelThreadHoverOpen}
                  onForward={onForward}
                  allMessages={allChannelMessages || messages}
                  channelId={channelId}
                  onScrollToMessage={onScrollToMessage}
                  messageRefs={messageRefs}
                  onRetryMessage={onRetryMessage}
                  onMeetingMessageUpdated={onMeetingMessageUpdated}
                  onEditEvent={onEditEvent}
                  onCloneEvent={onCloneEvent}
                  cloningEventId={cloningEventId}
                  expandCallNotesMeetingId={expandCallNotesMeetingId}
                  onJoinDirectCall={onJoinDirectCall}
                  reminderPanelOpen={reminderPanelOpen}
                  searchHighlightQuery={searchHighlightQuery}
                  activeSearchMatchMessageId={activeSearchMatchMessageId}
                  assignableUsers={assignableUsers}
                  chatMappingActive={chatMappingActive}
                  onMapMessage={onMapMessage}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MessageList;
